import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type DependencyList,
  type ReactNode,
} from 'react';

/*
 * PageHeader context — surfaces per-page title / subtitle / primary action
 * to the AdminLayout TopBar.
 *
 *   function MyPage() {
 *     useSetPageHeader({ title: 'Bookings', sub: '12 today', actions: <Button>New</Button> });
 *     return <…/>;
 *   }
 *
 * Pages without an explicit hook call fall through to the route-based
 * default title resolved by AdminLayout (see `routeTitle`).
 */

export interface PageHeaderState {
  title?: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
}

interface PageHeaderContextValue extends PageHeaderState {
  setHeader: (state: PageHeaderState) => void;
}

const PageHeaderContext = createContext<PageHeaderContextValue | null>(null);

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PageHeaderState>({});
  const value = useMemo<PageHeaderContextValue>(
    () => ({ ...state, setHeader: setState }),
    [state],
  );
  return <PageHeaderContext.Provider value={value}>{children}</PageHeaderContext.Provider>;
}

export function usePageHeader(): PageHeaderState {
  const ctx = useContext(PageHeaderContext);
  return ctx ?? {};
}

export function useSetPageHeader(state: PageHeaderState, deps: DependencyList = []) {
  const ctx = useContext(PageHeaderContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.setHeader(state);
    return () => ctx.setHeader({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
